import { Link } from 'react-router-dom';
import { schedules } from '../../mocks/schedules';
import { cohortInfo } from '../../mocks/cohort';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/af499ab4a1c13539639c85762481a763.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        {/* Main content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-5xl mx-auto px-6 text-center text-white py-20">
          <div className="mb-8">
            <img 
              src="https://static.readdy.ai/image/92135410c386b830c0527d02664e611f/fc28bb6cb6ead7d96ef0654189724086.png" 
              alt="日本警備犬協会" 
              className="h-24 mx-auto mb-8"
            />
          </div>
          
          <div className="mb-10 space-y-4">
            <p className="text-3xl md:text-4xl font-bold">
              高収入を得ながら、<br className="md:hidden" />犬と働く道がある
            </p>
            <p className="text-2xl md:text-3xl font-bold">
              国際空港の安全を支える
            </p>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            爆発物探知犬ハンドラー<br />養成アカデミー
          </h1>
          
          <p className="text-xl md:text-2xl font-light mb-12">運営：一般社団法人日本警備犬協会</p>
          
          <a 
            href="#application" 
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-12 py-5 rounded-full text-lg font-bold transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
          >
            無料説明会に申し込む
          </a>
        </div>

        {/* Arrow at bottom */}
        <div className="relative z-10 flex justify-center pb-8 animate-bounce">
          <i className="ri-arrow-down-line text-white text-4xl"></i>
        </div>
      </section>

      {/* Opening Message */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center space-y-8">
            <p className="text-2xl md:text-3xl leading-relaxed font-bold text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              「犬と仕事がしたい」<br />
              そう思っているのに――
            </p>
            <p className="text-xl md:text-2xl leading-relaxed text-gray-700">
              収入の問題で踏み出せない。<br />
              その現実を、私はよく知っています。
            </p>
            <div className="pt-8 space-y-6 text-lg leading-relaxed text-gray-700">
              <p className="font-bold text-xl">けれど今は、以前とは違います。</p>
              
              <p>犬と働きながら、<br />
              きちんと収入を得て生きていく。<br />
              そのための現実的な道筋が、<br />
              ようやく日本でも形になり始めました。</p>
              
              <p>しかもそれは、<br />
              一部の特別な人だけの話ではありません。</p>
              
              <p>社会に必要とされる仕事として、<br />
              制度の後押しを受けながら<br />
              目指せる専門職になってきているのです。</p>
              
              <p className="pt-4">近年、国際的なセキュリティレベルの上昇により、<br />
              爆発物探知のニーズは急速に高まっています。</p>
              
              <p>爆発物探知犬は2026年2月に国に認められるようになり、<br />
              私たちは国土交通省より<br />
              EDD検査事業者の第一号として認定を受けました。</p>
              
              <a 
                href="https://www.mlit.go.jp/koku/koku_fr9_000068.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block text-teal-600 hover:text-teal-700 underline font-medium cursor-pointer"
              >
                国土交通省の認定について詳しく見る →
              </a>
              
              <div className="pt-6">
                <img 
                  src="https://static.readdy.ai/image/92135410c386b830c0527d02664e611f/c4f40c4d2cba3c8d921166fd3287d715.jpeg"
                  alt="国土交通省認定 爆発物探知犬"
                  className="w-full max-w-2xl mx-auto rounded-lg shadow-lg object-top"
                />
              </div>
              
              <p className="pt-6">この先では、<br />
              なぜこの仕事が高い社会的価値と収入を両立できるのか、<br />
              そして犬と働く人生を現実にするために、<br />
              どんな学び・資格・進み方が必要なのかを、順を追ってご覧いただけます。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section id="target" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            こんな方に読んでほしい
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="space-y-6">
              {[
                '本当は犬と仕事がしたいと思っている',
                'でも、現実問題(特に収入)で踏み出せない',
                '「好きなことでは食べていけない」と自分に言い聞かせている',
                '犬といる時間だけが、本当の自分に戻れる時間だ'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl flex-shrink-0 mt-1"></i>
                  <p className="text-lg text-gray-700">{item}</p>
                </div>
              ))}
            </div>
            
            <div className="space-y-6">
              {[
                '会社の仕事をしながら、どこかで別の人生を想像している',
                '何年も「いつかは犬の仕事を」と思い続けている',
                'でも家族や生活を守る責任があり、挑戦できない'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl flex-shrink-0 mt-1"></i>
                  <p className="text-lg text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-12 rounded-2xl text-center">
            <p className="text-2xl md:text-3xl font-bold mb-6 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              このページは――
            </p>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              「犬と仕事がしたい。でもできない。」<br />
              その葛藤を抱え続けている人のためのものです。
            </p>
            <div className="pt-8 space-y-4 text-lg text-gray-700">
              <p>安定はある。</p>
              <p>生活も守れている。</p>
              <p className="font-bold text-xl">けれど、</p>
              <p>心のどこかで諦めきれていない。</p>
              <p className="text-xl font-bold mt-8 text-teal-700">
                もしその想いが消えていないのなら、<br />
                あなたはまだ終わっていません。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            こんな苦しみはありませんか？
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              '毎朝スーツに着替えながら「本当は犬と働きたい」と思っている',
              '愛犬の才能を感じているのに、それを活かす場所がない',
              'ドッグトレーナーになりたい気持ちはあるが「食べていけない」と諦めている',
              '犬と仕事をしている人を見るたびに、羨ましさと劣等感が混じる',
              '「好きなことを仕事にするなんて甘い」と周囲に言われてきた',
              '今の収入を失うのが怖くて、挑戦を正当化できない',
              '犬と過ごす時間が一番幸せなのに、それが"趣味"で終わっている',
              '犬の能力が眠ったまま年を重ねていくことに焦りを感じる',
              'このまま定年を迎えたら、きっと後悔する気がしている',
              '「本気で挑戦しなかった自分」を将来責める気がしている'
            ].map((pain, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-teal-600">
                <p className="text-gray-700 leading-relaxed">{pain}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center space-y-6 max-w-4xl mx-auto">
            <p className="text-2xl font-bold text-gray-800">これは単なる転職の悩みではありません。</p>
            <p className="text-3xl font-bold text-teal-700" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              "自分の人生の使い方"の問題です。
            </p>
            <div className="pt-8 space-y-4 text-lg text-gray-700">
              <p>私もかつて、</p>
              <p>安定と情熱の間で揺れ続けていました。</p>
              <p className="font-bold text-xl">しかし本当につらかったのは、</p>
              <p className="font-bold text-xl">挑戦しない自分をどこかで正当化していたことでした。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            私の話を少しさせてください
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://japank9-academy.com/wp/wp-content/themes/academy/images/sec05_kpic02.jpg"
                alt="川野信哉" 
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p className="text-2xl font-bold text-gray-800">初めまして。日本警備犬協会の川野です。</p>
              <p>私は元々、機械のエンジニアとして大手ゼネコンで働いていました。</p>
              <p>安定した環境でした。</p>
              <p>仕事も嫌いではありませんでした。</p>
              <p className="font-bold text-xl text-teal-700">しかし――</p>
              <p>ふと、昔飼っていた犬のことが浮かび、「犬と一緒に働くんだ」という心の声が聞こえた時、もうこの会社には居られないと思いました。</p>
            </div>
          </div>
          
          <div className="space-y-8 text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
            <p>30歳を過ぎてからの転職は周囲から猛反対を受けました。</p>
            <p>渡米し、フランス人の師匠に学び、ビジネスになる保証もないままドッグトレーナーとして独立しました。</p>
            
            <div className="bg-gray-50 p-8 rounded-lg my-8">
              <p className="text-xl font-bold mb-4 text-gray-800">しかし現実は厳しかった。</p>
              <ul className="space-y-3">
                <li>• レッスンは打ち切られ、</li>
                <li>• 価格で比べられ、</li>
                <li>• 自分の強みも分からない。</li>
              </ul>
              <p className="mt-6">作業犬の世界を横目に見ながら、「自分には無理だ」とどこかで決めつけていました。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Turning Point */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            世界が変わった瞬間
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-2xl font-bold text-teal-700 text-center">転機は「鼻の作業」でした。</p>
            
            <p>災害救助犬、麻薬探知、足跡追及、臭気選別、そして現在取り組んでいる爆発物探知。</p>
            <p className="font-bold text-xl">すべては「犬の鼻」を使う仕事です。</p>
            
            <div className="bg-teal-50 p-8 rounded-lg my-8">
              <p className="text-xl font-bold mb-4 text-gray-800">鼻の作業は特殊です。</p>
              <p className="mb-4">人が答えを持っていない。</p>
              <p className="mb-4">答えは犬だけが知っている。</p>
              <p className="font-bold">だからこそ、犬を観察し、理解し、信じる力が必要になる。</p>
            </div>
            
            <p>私はそこで初めて、犬の本質を理解し始めました。</p>
            
            <div className="space-y-4 pl-6 border-l-4 border-teal-600">
              <p>犬の個性が見え、</p>
              <p>感情が見え、</p>
              <p>やる気の本質が見えるようになった。</p>
            </div>
            
            <p className="text-2xl font-bold text-center text-gray-800 pt-8">そして不思議なことに――</p>
            <p className="text-2xl font-bold text-center text-teal-700">自分にも自信が芽生えていったのです。</p>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            その先にあった答え
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8 text-lg text-gray-700 leading-relaxed">
            <p>鼻の技術を突き詰めた先にあるのが爆発物探知犬(EDD)業務です。</p>
            
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-10 rounded-2xl my-12 text-center">
              <p className="text-2xl font-bold mb-4">私たちは</p>
              <p className="text-3xl font-bold mb-6">国土交通省</p>
              <p className="text-xl mb-2">より航空貨物分野における</p>
              <p className="text-2xl font-bold">認定を受けました。</p>
            </div>
            
            <p className="text-xl">国際空港という国防の最前線で、犬の鼻を使い人々の安全を守る。</p>
            
            <div className="space-y-4 bg-gray-50 p-8 rounded-lg">
              <p className="font-bold text-xl text-gray-800">これは理想論ではありません。</p>
              <p className="font-bold text-xl text-gray-800">実務です。</p>
              <p className="text-2xl font-bold text-teal-700 pt-4">
                そして継続的に従事すれば<br />
                年収700万円水準も現実的な専門職です。
              </p>
            </div>
            
            <p className="text-2xl font-bold text-center text-gray-800 pt-8">
              犬と仕事をしながら、<br />
              高い収入を得る道がここにあります。
            </p>
            
            <div className="pt-8">
              <img 
                src="https://static.readdy.ai/image/92135410c386b830c0527d02664e611f/09aebf326c5ff11b3c4fecebc1b0c4d9.jpeg"
                alt="爆発物探知犬ハンドラー"
                className="w-full max-w-2xl mx-auto rounded-lg shadow-lg object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recruitment */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            爆発物探知犬(EDD)ハンドラー募集
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl text-center">
              今、航空貨物の会社から30件以上の問い合わせがあり、<br />
              圧倒的にハンドラーと犬が足りていない状況です。
            </p>
            
            <div className="bg-white p-10 rounded-2xl shadow-lg text-center">
              <p className="text-2xl font-bold text-gray-800 mb-6">
                一緒にやっていく仲間を育てることが急務となっています。
              </p>
              <p className="text-xl mb-4">ハンドラーと犬の養成をおこなっているのが</p>
              <p className="text-3xl font-bold text-teal-700 mb-6">爆発物探知犬ハンドラー養成アカデミーです。</p>
              <p className="text-xl">次は{cohortInfo.cohortLabel}を募集します。</p>
              <p className="text-xl font-bold text-gray-800 mt-4">{cohortInfo.cohortLabel}は{cohortInfo.startMonth}より定員{cohortInfo.capacity}名で開催します。</p>
            </div>
            
            <p className="text-center text-xl pt-8">
              説明会を個別でやっているので、<br />
              下記条件をみたした方は説明会に申し込みをしてください。
            </p>

            {/* 説明会日程カレンダー */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-teal-600 px-8 py-5 flex items-center space-x-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-calendar-event-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white">無料説明会 開催日程</h3>
              </div>
              <div className="p-8 space-y-4">
                {schedules.map((session, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded-xl px-6 py-5 border border-gray-100">
                    <div className="flex items-center space-x-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                        <i className="ri-time-line text-teal-600 text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-800">{session.date}</p>
                        <p className="text-lg text-teal-700 font-medium">{session.time}</p>
                        <p className="text-sm text-gray-500">{session.format}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`text-sm font-bold px-3 py-1 rounded-full whitespace-nowrap ${session.status === '残りわずか' ? 'bg-orange-100 text-orange-600' : session.status === '満席' ? 'bg-red-100 text-red-500' : 'bg-teal-100 text-teal-700'}`}>
                        {session.status}
                      </span>
                      {session.status !== '満席' ? (
                        <a
                          href="#application"
                          className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold px-5 py-2 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          申し込む
                        </a>
                      ) : (
                        <span className="bg-gray-200 text-gray-400 text-sm font-bold px-5 py-2 rounded-full whitespace-nowrap">
                          受付終了
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-sm text-gray-500 text-center pt-2">
                  ※ 日程は変更になる場合があります。お早めにお申し込みください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            対象条件
          </h2>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6 mb-12">
              {/* 中型犬以上を飼育している - 補足付き */}
              <div className="flex items-start space-x-4 bg-gradient-to-r from-teal-50 to-white p-6 rounded-lg border-l-4 border-teal-600">
                <i className="ri-shield-check-fill text-teal-600 text-3xl flex-shrink-0 mt-1"></i>
                <div>
                  <p className="text-xl font-medium text-gray-800">中型犬以上を飼育している</p>
                  <p className="text-sm text-teal-700 mt-1">（これから犬を迎える方、相談にのります。）</p>
                </div>
              </div>
              {[
                '自ら服従訓練を仕上げられる',
                '覚悟を持って自己投資できる',
                '日本の安全を守る仕事に誇りを持てる'
              ].map((req, index) => (
                <div key={index} className="flex items-center space-x-4 bg-gradient-to-r from-teal-50 to-white p-6 rounded-lg border-l-4 border-teal-600">
                  <i className="ri-shield-check-fill text-teal-600 text-3xl flex-shrink-0"></i>
                  <p className="text-xl font-medium text-gray-800">{req}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-gray-800 text-white p-10 rounded-2xl text-center">
              <p className="text-2xl font-bold mb-4">これは簡単な道ではありません。</p>
              <p className="text-3xl font-bold text-teal-400">
                しかし、本気で取り組めば<br />
                人生を変える道になります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            このアカデミーで得られる<br />10のメリット
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { num: '01', title: '国家基準レベルの探知技術が身につく', desc: '国土交通省認定の基準に基づいた本格的な訓練技術を習得できます。' },
              { num: '02', title: '価格競争から脱却できる専門職になれる', desc: '高度な専門性により、価格ではなく技術で選ばれる存在になります。' },
              { num: '03', title: '継続業務で安定収入を目指せる', desc: '航空貨物会社との継続契約により、安定した収入基盤を構築できます。' },
              { num: '04', title: '年収700万円水準が視野に入る', desc: '専門職として継続的に従事することで、高収入を実現できます。' },
              { num: '05', title: '国際空港という最前線で働ける可能性', desc: '日本の安全を守る最前線で、社会貢献度の高い仕事に携われます。' },
              { num: '06', title: '犬の本質を深く理解できる', desc: '鼻の作業を通じて、犬の感情や個性を深いレベルで理解できるようになります。' },
              { num: '07', title: '犬のやる気を科学的に引き出せる', desc: 'モチベーション管理や訓練の積み重ね方を体系的に学べます。' },
              { num: '08', title: '犬の感情を読み解く力が身につく', desc: '細かな変化を観察する力が養われ、犬との信頼関係が深まります。' },
              { num: '09', title: '愛犬の個性を最大限に活かせる', desc: '犬それぞれの特性を理解し、強みを引き出す技術が身につきます。' },
              { num: '10', title: '「犬と社会に貢献している」という誇りを持てる', desc: '好きなことで社会に貢献できる、かけがえのない充実感を得られます。' }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {benefit.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            受講者の声
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* 山崎百合香 */}
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex justify-center mb-6">
                <img 
                  src="https://cdn.peraichi.com/userData/0ff415a3-f5fa-4a6c-9370-cba6df41cc2c/img/64dc3c1395f01/original.jpeg"
                  alt="山崎百合香" 
                  className="w-32 h-32 rounded-full object-cover"
                />
              </div>
              <div className="text-center mb-6">
                <p className="text-xl font-bold text-gray-800">山崎百合香</p>
                <p className="text-gray-600">犬名: みるど(B・マリノア)</p>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                活躍できた事で自信にも繋がり、人生において大切な仲間ができた
              </h3>
              <p className="text-gray-700 leading-relaxed">
                犬の鼻を使ったトレーニングにとても興味があったので、鼻を活かした訓練で仕事がしたいと思い、仔犬を爆発物探知犬として育て始めました。生後4ヶ月頃から開始した合同訓練では、愛犬の成長段階の確認と次回の訓練までの課題を明確にしてもらうこととで、楽しくレベルアップができました。自分の犬に近い月齢の犬の様子を見ることで、自分の犬には何の訓練が足りていないのかなど気が付けることも多くありました。
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                認定試験とクライアント試験もクリアし、オリンピックの警備の仕事をもらうことができました。本番と同じような緊張感でも自分と犬のコンビでできるようになり犬との絆もより深まったように思います。
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                自分1人で訓練していたら、訓練のステップなども飛ばしすぎてしまったり、自分が課題だと感じているところは実は焦点がずれてしまっていたりしていたかと思いますが、リモートや対面での定期的な訓練や座学のおかげで、安心して実働することができました。実働のお仕事をもらい、活躍できた事は自分の自信にも繋がりましたし、一緒に実働をした仲間は人生において大切な仲間となりました。
              </p>
            </div>
            
            {/* 田中幸希 */}
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex justify-center mb-6">
                <img 
                  src="https://cdn.peraichi.com/userData/0ff415a3-f5fa-4a6c-9370-cba6df41cc2c/img/64dc3eadb604a/original.jpeg"
                  alt="田中幸希" 
                  className="w-32 h-32 rounded-full object-cover"
                />
              </div>
              <div className="text-center mb-6">
                <p className="text-xl font-bold text-gray-800">田中幸希</p>
                <p className="text-gray-600">犬名: れおな(B・マリノア)</p>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                犬が教えてくれることとはこういうことなのか!
              </h3>
              <p className="text-gray-700 leading-relaxed">
                犬の鼻を使う作業の訓練が初めてだったので不安もありましたが、実践の訓練とオンラインの座学との両方から学ぶことが出来た事で理解が深まりやすかったです。特にニオイの流れや広がり方、犬のボディーランゲージについては、「こんな場合もあるのか」と新たな発見が多かったです。
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                犬によっても、環境や時間によっても、「ニオイへの反応」や「ニオイの流れ」は全く同じということは絶対にないので、他の受講者の方々と一緒に様々な反応が見れたことがとても勉強になり、(犬が教えてくれることとはこういうことなのか!)と感じる部分が多かったです。
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                普段は自分の仕事がある為、オンラインで好きな時間に好きな場所で座学を受講が出来たのも助かりました。他の方の訓練も仕事の合間にオンラインで拝見することが出来たこともとても有意味でした。オリンピック関係での実働の機会も頂き、とても大きな経験をさせて頂きました。今後も学ばせて頂いた事を土台に、犬の持つ能力を活かしていけるよう精進していきたいと思います。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trainer Introduction */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            トレーナー紹介
          </h2>
          
          <div className="space-y-12">
            <div className="bg-white p-10 rounded-xl shadow-lg">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="text-center">
                  <img 
                    src="https://japank9.com/wp-content/themes/japank9/images/bm_pic02.jpg"
                    alt="川野悌子" 
                    className="w-48 h-auto mx-auto rounded-lg mb-4"
                  />
                  <p className="text-sm text-gray-600 mb-2">講師</p>
                  <p className="text-2xl font-bold text-gray-800">川野 悌子</p>
                </div>
                <div className="md:col-span-2 space-y-4 text-gray-700">
                  <div className="space-y-2 text-sm">
                    <p>• Explosive Dog Seminar (by K9 Speciality Search Associates & Sniffer Dog Company)/2016</p>
                    <p>• 甲種火薬類取扱保安責任者免状交付 /2018</p>
                    <p>• K9 Explosive detection course (by Japan security guard dog asso.) /2019</p>
                    <p>• The Odor Discrimination and Proofing (by HRD Specialized K9 Training)/ 2021</p>
                  </div>
                  <p className="leading-relaxed pt-4">
                    昔は犬が少し怖かった自分が、今では「犬達と働く事は天職だ」と思える程、人生が変わりました。結婚を機に犬が好きな夫と共に犬のトレーナーを目指す事になり、夫に先駆け米国カリフォルニア州のトレーニングセンターにて軍用犬、警察犬の訓練を学び始めます。犬のスピードや正確な作業を見て「なんてカッコイイのだろう。仕事に集中している姿は本当に美しい」と瞬時に心を奪われました。
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-10 rounded-xl shadow-lg">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="text-center">
                  <img 
                    src="https://japank9.com/wp-content/themes/japank9/images/bm_pic01.jpg"
                    alt="川野信哉" 
                    className="w-48 h-auto mx-auto rounded-lg mb-4"
                  />
                  <p className="text-sm text-gray-600 mb-2">代表理事</p>
                  <p className="text-2xl font-bold text-gray-800">川野 信哉</p>
                </div>
                <div className="md:col-span-2 space-y-4 text-gray-700">
                  <div className="space-y-2 text-sm">
                    <p>パリ日本人学校、同志社国際高校、同志社大学機械工学部卒</p>
                    <p>鹿島建設にて10年間エンジニアとして従事。</p>
                    <p>今までとは違う分野で好きなことをやりたいと犬の仕事にすると決意。</p>
                  </div>
                  <p className="leading-relaxed pt-4">
                    エンジニアとしてのキャリアから転身。フランス人訓練士に師事し訓練を開始します。命を賭けてマリノアと真剣に向き合うことで生まれた「生きている感覚と充実感」に感動し、それ以後マリノアを日本の社会でどう生かすかを考えて続けました。家庭犬を対象として、犬のがっこうECO-LE(エコール)を創業し、311を機に災害救助犬を育成。広島土砂災害や熊本地震などへ出動します。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application" className="py-24 bg-gradient-to-b from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              無料説明会お申込み
            </h2>
            <p className="text-xl text-white">
              ドッグトレーナー経験が少なくても、<br />
              お客様があなたにお願いしたくなる
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl p-10">
            <form 
              id="academy-application-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d6jv4kec9mmd5omn5sc0"
              method="POST"
              className="space-y-6"
            >
              <div>
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  お名前 <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="お名前"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="山田 太郎"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  メールアドレス <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="example@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                  電話番号 <span className="text-red-600">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="電話番号"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="090-1234-5678"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-3">
                  ご希望の説明会日程 <span className="text-red-600">*</span>
                </label>
                <div className="space-y-3">
                  {schedules
                    .filter((s) => s.status !== '満席')
                    .map((session, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-4 bg-gray-50 border border-gray-200 rounded-lg px-5 py-4 cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 has-[:checked]:border-teal-500 has-[:checked]:bg-teal-50"
                      >
                        <input
                          type="radio"
                          name="希望日程"
                          value={`${session.date} ${session.time}（${session.format}）`}
                          required
                          className="accent-teal-600 w-4 h-4 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <p className="font-bold text-gray-800">{session.date}　{session.time}</p>
                          <p className="text-sm text-gray-500">{session.format}</p>
                        </div>
                        <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${session.status === '残りわずか' ? 'bg-orange-100 text-orange-600' : session.status === '満席' ? 'bg-red-100 text-red-500' : 'bg-teal-100 text-teal-700'}`}>
                          {session.status}
                        </span>
                      </label>
                    ))}
                  {schedules.every((s) => s.status === '満席') && (
                    <p className="text-center text-gray-500 py-4">現在申し込み可能な日程がありません。</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="dog-info" className="block text-gray-700 font-medium mb-2">
                  飼育している犬の情報
                </label>
                <textarea
                  id="dog-info"
                  name="犬の情報"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="犬種、年齢、訓練経験など(500文字以内)"
                ></textarea>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                  ご質問・ご要望
                </label>
                <textarea
                  id="message"
                  name="ご質問・ご要望"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="説明会で聞きたいことなど(500文字以内)"
                ></textarea>
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  無料説明会に申し込む
                </button>
              </div>
              
              <p className="text-sm text-gray-600 text-center pt-4">
                送信いただいた情報は、個人情報保護方針に基づき適切に管理いたします。
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            最後に
          </h2>
          
          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl">犬と仕事がしたい。</p>
            <p className="text-xl">でも収入が不安。</p>
            <p className="text-xl">そのジレンマに、私は長年苦しみました。</p>
            
            <p className="text-2xl font-bold text-gray-800 pt-8">だからこそ言えます。</p>
            
            <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-12 rounded-2xl my-12">
              <p className="text-3xl md:text-4xl font-bold text-teal-700 mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                犬と仕事をしながら、<br />
                高い収入を得る道は<br />
                確かに存在します。
              </p>
              <p className="text-xl text-gray-700">
                あなたの想いを、現実にする時が来ました。
              </p>
            </div>
            
            <div className="pt-8">
              <a 
                href="#application" 
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-12 py-5 rounded-full text-lg font-bold transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
              >
                無料説明会に申し込む
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">一般社団法人日本警備犬協会</h3>
              <div className="space-y-2 text-gray-300">
                <p>〒108-0023</p>
                <p>東京都港区芝浦4-21-1</p>
                <p className="pt-4">代表理事: 川野信哉</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">リンク</h3>
              <div className="space-y-2">
                <p>
                  <a href="https://japank9.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                    日本警備犬協会サイト
                  </a>
                </p>
                <p>
                  <a href="https://japank9.com/tokutei" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                    特定商取引法に基づく表記
                  </a>
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; 2025 一般社団法人日本警備犬協会 All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}